import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Filter, Plus, Trash2, Eye, Send, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Invoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitAmount, setSubmitAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/invoices${params}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/invoices/generate-batch`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      console.error('Error generating invoices:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${API_URL}/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailDialog(true);
  };

  const openSubmitDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setSubmitAmount(invoice.amount > 0 ? invoice.amount.toString() : '');
    setShowSubmitDialog(true);
  };

  const handleSubmitInvoice = async () => {
    if (!selectedInvoice) return;
    
    const amount = parseFloat(submitAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/invoices/${selectedInvoice.invoice_id}/submit`, { amount });
      toast.success('Invoice submitted successfully');
      setShowSubmitDialog(false);
      setSelectedInvoice(null);
      setSubmitAmount('');
      fetchInvoices();
    } catch (error) {
      console.error('Error submitting invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    if (invoice.status === 'draft') {
      toast.error('Cannot download draft invoices');
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoice.invoice_id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.reference || invoice.invoice_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'open':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'draft':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      default:
        return 'bg-zinc-800 text-zinc-400 border-zinc-700';
    }
  };

  const handleRevertToDraft = async (invoiceId) => {
    if (!window.confirm('Remettre cette facture en brouillon ? Le VA devra ressaisir le montant.')) return;
    
    try {
      await axios.put(`${API_URL}/invoices/${invoiceId}/revert-to-draft`);
      toast.success('Invoice reverted to draft');
      fetchInvoices();
    } catch (error) {
      console.error('Error reverting invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to revert invoice');
    }
  };

  // Calculate totals
  const totalOpen = invoices.filter(i => i.status === 'open').reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const totalDraft = invoices.filter(i => i.status === 'draft').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="invoices-title">
            {isAdmin ? 'Invoices' : 'My Invoices'}
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            {isAdmin ? 'Manage VA invoices and track payments' : 'View your invoices and payment status'}
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={handleGenerateBatch}
            disabled={generating}
            className="bg-white text-black hover:bg-zinc-200"
            data-testid="generate-invoices-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {generating ? 'Generating...' : 'Generate Last Week Invoices'}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-md border border-border">
          <div className="text-sm text-zinc-400 mb-1">Total Invoices</div>
          <div className="text-2xl font-bold text-white">{invoices.length}</div>
        </div>
        {totalDraft > 0 && (
          <div className="bg-card p-4 rounded-md border border-amber-500/20">
            <div className="text-sm text-zinc-400 mb-1">Drafts</div>
            <div className="text-2xl font-bold text-amber-500">{totalDraft}</div>
          </div>
        )}
        <div className="bg-card p-4 rounded-md border border-blue-500/20">
          <div className="text-sm text-zinc-400 mb-1">Open Amount</div>
          <div className="text-2xl font-bold text-blue-500">${totalOpen.toFixed(2)}</div>
        </div>
        <div className="bg-card p-4 rounded-md border border-green-500/20">
          <div className="text-sm text-zinc-400 mb-1">Paid Amount</div>
          <div className="text-2xl font-bold text-green-500">${totalPaid.toFixed(2)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">Filter:</span>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-white" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-white hover:bg-zinc-800">All</SelectItem>
            <SelectItem value="draft" className="text-white hover:bg-zinc-800">Draft</SelectItem>
            <SelectItem value="open" className="text-white hover:bg-zinc-800">Open</SelectItem>
            <SelectItem value="paid" className="text-white hover:bg-zinc-800">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoices Table */}
      {invoices.length === 0 ? (
        <div className="bg-card p-12 rounded-md border border-border text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No invoices yet</h3>
          <p className="text-muted-foreground">
            {isAdmin ? 'Generate invoices using the button above' : 'Your invoices will appear here once generated'}
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Reference</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Sender</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Period</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Due Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow
                  key={invoice.invoice_id}
                  className="border-b border-border table-row-hover"
                  data-testid={`invoice-row-${invoice.invoice_id}`}
                >
                  <TableCell className="font-mono text-sm text-white font-medium">
                    {invoice.reference || invoice.invoice_id.substring(0, 8)}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-white">{invoice.va_name}</TableCell>
                  )}
                  <TableCell className="text-zinc-400 text-sm">
                    {invoice.va_role || 'Threads VA'}
                  </TableCell>
                  <TableCell className="text-zinc-300 text-sm">
                    {invoice.period_start && invoice.period_end ? (
                      <>
                        {format(new Date(invoice.period_start), 'MMM dd')} - {format(new Date(invoice.period_end), 'MMM dd, yyyy')}
                      </>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {invoice.status === 'draft' ? (
                      <div className="text-amber-500 font-semibold">To fill</div>
                    ) : (
                      <>
                        <div className="text-white font-semibold">${invoice.amount?.toFixed(2) || '0.00'}</div>
                        {invoice.bonus > 0 && (
                          <div className="text-xs text-green-500">incl. bonus +${invoice.bonus?.toFixed(2)}</div>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`border px-2 py-0.5 rounded text-xs font-medium uppercase ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {invoice.due_date ? format(new Date(invoice.due_date), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {invoice.status === 'draft' && !isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openSubmitDialog(invoice)}
                          className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
                          data-testid={`submit-invoice-${invoice.invoice_id}`}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewInvoiceDetails(invoice)}
                        className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                        data-testid={`view-invoice-${invoice.invoice_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadPdf(invoice)}
                        disabled={invoice.status === 'draft'}
                        className={invoice.status === 'draft' 
                          ? "text-zinc-600 cursor-not-allowed" 
                          : "text-blue-500 hover:text-blue-400 hover:bg-blue-900/20"}
                        title={invoice.status === 'draft' ? "Cannot download draft" : "Download PDF"}
                        data-testid={`download-invoice-${invoice.invoice_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {isAdmin && invoice.status === 'open' && invoice.va_role !== 'Threads VA' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevertToDraft(invoice.invoice_id)}
                          className="text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
                          title="Revert to draft"
                          data-testid={`revert-invoice-${invoice.invoice_id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && (invoice.status === 'open' || invoice.status === 'draft') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                          data-testid={`delete-invoice-${invoice.invoice_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Invoice Details</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedInvoice?.reference || selectedInvoice?.invoice_id?.substring(0, 8)}
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Sender</div>
                  <div className="text-white font-medium">{selectedInvoice.va_name}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Mail</div>
                  <div className="text-white">{selectedInvoice.va_email}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Role</div>
                  <div className="text-white">{selectedInvoice.va_role || 'Threads VA'}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Period</div>
                  <div className="text-white text-sm">
                    {selectedInvoice.period_start && (
                      <>
                        {format(new Date(selectedInvoice.period_start), 'MMM dd')} - {format(new Date(selectedInvoice.period_end), 'MMM dd, yyyy')}
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="border-t border-zinc-800 pt-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Description</div>
                <div className="text-white">{selectedInvoice.description || 'Services'}</div>
              </div>

              <div className="border-t border-zinc-800 pt-4 space-y-2">
                {selectedInvoice.status === 'draft' ? (
                  <div className="text-center py-4">
                    <div className="text-amber-500 mb-2">Draft Invoice - Amount to be filled</div>
                    <Button
                      onClick={() => {
                        setShowDetailDialog(false);
                        openSubmitDialog(selectedInvoice);
                      }}
                      className="bg-amber-500 text-black hover:bg-amber-400"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Fill & Submit Invoice
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Base Amount</span>
                      <span className="text-white">${selectedInvoice.base_amount?.toFixed(2)}</span>
                    </div>
                    {selectedInvoice.bonus > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Bonus</span>
                        <span className="text-green-500">+${selectedInvoice.bonus?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-zinc-800 pt-2">
                      <span className="text-white font-semibold">Total</span>
                      <span className="text-white font-bold text-lg">${selectedInvoice.amount?.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-zinc-800 pt-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Status</div>
                  <span className={`border px-2 py-0.5 rounded text-xs font-medium uppercase ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Due Date</div>
                  <div className="text-white">{selectedInvoice.due_date && format(new Date(selectedInvoice.due_date), 'MMM dd, yyyy')}</div>
                </div>
              </div>

              {selectedInvoice.status === 'paid' && selectedInvoice.paid_at && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-md p-3">
                  <div className="text-xs text-green-500 uppercase">Paid On</div>
                  <div className="text-green-400">{format(new Date(selectedInvoice.paid_at), 'MMM dd, yyyy HH:mm')}</div>
                </div>
              )}

              {selectedInvoice.va_wallet_address && (
                <div>
                  <div className="text-xs text-zinc-500 uppercase">Wallet</div>
                  <div className="text-zinc-400 font-mono text-sm truncate">{selectedInvoice.va_wallet_address}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Invoice Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Submit Invoice</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter the amount for this invoice and submit it for payment
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-zinc-800/50 rounded-md p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Reference:</span>
                  <span className="text-white font-mono">{selectedInvoice.reference}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Period:</span>
                  <span className="text-white">
                    {selectedInvoice.period_start && format(new Date(selectedInvoice.period_start), 'MMM dd')} - {selectedInvoice.period_end && format(new Date(selectedInvoice.period_end), 'MMM dd, yyyy')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Due Date:</span>
                  <span className="text-white">{selectedInvoice.due_date && format(new Date(selectedInvoice.due_date), 'MMM dd, yyyy')}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">Amount (USD)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={submitAmount}
                  onChange={(e) => setSubmitAmount(e.target.value)}
                  placeholder="Enter invoice amount"
                  className="bg-zinc-900/50 border-zinc-800 text-white text-lg font-semibold"
                  data-testid="submit-amount-input"
                />
              </div>
              
              <Button
                onClick={handleSubmitInvoice}
                disabled={submitting || !submitAmount}
                className="w-full bg-amber-500 text-black hover:bg-amber-400"
                data-testid="confirm-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? 'Submitting...' : 'Submit Invoice'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
